/**
 * Timeline Context
 *
 * Provides synchronized zoom/pan state across all timeline components.
 * Both BEFORE and AFTER timelines consume this context to stay perfectly in sync.
 */

import { createContext, useContext, useReducer, useCallback, useMemo, useEffect, useRef, type ReactNode } from 'react'
import type { TimelineContextValue } from './types'
import type { TimelineTransform, TimelineDomain, ZoomConstraints } from '../types'
import { timelineReducer, createInitialState } from './timelineReducer'
import { DEFAULT_ZOOM_CONSTRAINTS } from './types'

const TimelineContext = createContext<TimelineContextValue | null>(null)

/**
 * Calculate effective zoom scale constraints from duration-based constraints.
 *
 * @param domainSpan - The span of the domain in milliseconds
 * @param constraints - The raw constraints (may include duration-based values)
 * @returns Constraints with calculated minScale/maxScale
 */
function calculateEffectiveConstraints(
  domainSpan: number,
  constraints: Partial<ZoomConstraints>
): ZoomConstraints {
  const merged = { ...DEFAULT_ZOOM_CONSTRAINTS, ...constraints }

  let { minScale, maxScale } = merged
  const { minVisibleDuration, maxVisibleDuration, zoomStep } = merged

  // Calculate minScale from maxVisibleDuration (zoomed out = more time visible)
  // At scale=1, domain fills container. At smaller scale, more time visible.
  // scale = domainSpan / visibleDuration
  if (maxVisibleDuration && domainSpan > 0) {
    const calculatedMinScale = domainSpan / maxVisibleDuration
    // Clamp to reasonable bounds (don't zoom out so far timeline is tiny)
    minScale = Math.max(0.01, calculatedMinScale)
  }

  // Calculate maxScale from minVisibleDuration (zoomed in = less time visible)
  if (minVisibleDuration && domainSpan > 0) {
    const calculatedMaxScale = domainSpan / minVisibleDuration
    // Clamp to reasonable bounds (don't zoom in infinitely)
    maxScale = Math.min(1000, calculatedMaxScale)
  }

  // Ensure minScale <= 1 <= maxScale (scale=1 should always be valid)
  minScale = Math.min(minScale, 1)
  maxScale = Math.max(maxScale, 1)

  // Ensure minScale < maxScale
  if (minScale >= maxScale) {
    minScale = maxScale / 2
  }

  return { minScale, maxScale, zoomStep, minVisibleDuration, maxVisibleDuration }
}

interface TimelineProviderProps {
  children: ReactNode
  containerWidth: number
  constraints?: Partial<ZoomConstraints>
  initialDomain?: TimelineDomain
}

/**
 * Provider component for timeline context
 */
export function TimelineProvider({
  children,
  containerWidth,
  constraints,
  initialDomain,
}: TimelineProviderProps) {
  const [state, dispatch] = useReducer(
    timelineReducer,
    createInitialState(containerWidth, constraints)
  )

  // Track if this is the first domain set (initial mount)
  const isFirstDomainSet = useRef(true)

  // Store the latest domain for reset purposes (even when not auto-applied)
  const latestDomainRef = useRef(initialDomain)
  useEffect(() => {
    latestDomainRef.current = initialDomain
  }, [initialDomain])

  // Only set domain on first render, not on subsequent changes
  // This prevents pan/zoom jumping when toggle changes
  useMemo(() => {
    if (initialDomain && isFirstDomainSet.current) {
      dispatch({ type: 'SET_DOMAIN', payload: initialDomain })
      isFirstDomainSet.current = false
    }
  }, [initialDomain])

  // Update container width when it changes
  useMemo(() => {
    dispatch({ type: 'SET_CONTAINER_WIDTH', payload: containerWidth })
  }, [containerWidth])

  // Calculate effective constraints when domain changes
  // This converts duration-based constraints to actual scale factors
  useEffect(() => {
    const domainSpan = state.domain.span
    if (domainSpan > 0) {
      const effectiveConstraints = calculateEffectiveConstraints(domainSpan, constraints || {})
      dispatch({ type: 'SET_CONSTRAINTS', payload: effectiveConstraints })
    }
  }, [state.domain.span, constraints])

  /**
   * Convert a date to X pixel position within the viewport
   */
  const dateToX = useCallback(
    (date: Date | string | number | null): number => {
      if (date === null) return 0

      const timestamp = typeof date === 'number' ? date :
        date instanceof Date ? date.getTime() :
        new Date(date).getTime()

      const { domain, transform, containerWidth: width } = state
      const { minDate, maxDate } = domain
      const totalSpan = maxDate - minDate

      // Base position (0-1 range within domain)
      const normalizedPosition = (timestamp - minDate) / totalSpan

      // Apply transform: scale and translate
      const baseX = normalizedPosition * width
      const scaledX = baseX * transform.k + transform.x

      return scaledX
    },
    [state]
  )

  /**
   * Convert an X pixel position to a Date
   */
  const xToDate = useCallback(
    (x: number): Date => {
      const { domain, transform, containerWidth: width } = state
      const { minDate, maxDate } = domain
      const totalSpan = maxDate - minDate

      // Reverse the transform
      const baseX = (x - transform.x) / transform.k
      const normalizedPosition = baseX / width

      // Convert to timestamp
      const timestamp = minDate + normalizedPosition * totalSpan
      return new Date(timestamp)
    },
    [state]
  )

  /**
   * Get pixel bounds for a segment given its date range
   */
  const getSegmentBounds = useCallback(
    (from: string | null, to: string | null): { x: number; width: number } => {
      const { domain } = state
      const { minDate, maxDate } = domain

      // Handle null dates: from=null means start of timeline, to=null means end
      const startTimestamp = from ? new Date(from).getTime() : minDate
      const endTimestamp = to ? new Date(to).getTime() : maxDate

      const startX = dateToX(startTimestamp)
      const endX = dateToX(endTimestamp)

      // Ensure minimum width for visibility
      const minWidth = 20
      const calculatedWidth = Math.max(endX - startX, minWidth)

      return {
        x: startX,
        width: calculatedWidth,
      }
    },
    [state, dateToX]
  )

  // Action handlers
  const zoomIn = useCallback(() => dispatch({ type: 'ZOOM_IN' }), [])
  const zoomOut = useCallback(() => dispatch({ type: 'ZOOM_OUT' }), [])

  // Reset: update domain to latest (includes background segments if visible) AND reset transform
  const reset = useCallback(() => {
    if (latestDomainRef.current) {
      dispatch({ type: 'SET_DOMAIN', payload: latestDomainRef.current })
    }
    dispatch({ type: 'RESET' })
  }, [])

  const setTransform = useCallback((transform: TimelineTransform) => {
    dispatch({ type: 'SET_TRANSFORM', payload: transform })
  }, [])

  const pan = useCallback((deltaX: number) => {
    dispatch({ type: 'PAN', payload: deltaX })
  }, [])

  const setDomain = useCallback((domain: TimelineDomain) => {
    dispatch({ type: 'SET_DOMAIN', payload: domain })
  }, [])

  const setContainerWidth = useCallback((width: number) => {
    dispatch({ type: 'SET_CONTAINER_WIDTH', payload: width })
  }, [])

  // Computed values for zoom limits
  const canZoomIn = state.transform.k < state.constraints.maxScale
  const canZoomOut = state.transform.k > state.constraints.minScale

  const value: TimelineContextValue = useMemo(
    () => ({
      state,
      dateToX,
      xToDate,
      getSegmentBounds,
      zoomIn,
      zoomOut,
      reset,
      setTransform,
      pan,
      setDomain,
      setContainerWidth,
      canZoomIn,
      canZoomOut,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, canZoomIn, canZoomOut]
  )

  return (
    <TimelineContext.Provider value={value}>
      {children}
    </TimelineContext.Provider>
  )
}

/**
 * Hook to access timeline context
 * @throws Error if used outside TimelineProvider
 */
export function useTimelineContext(): TimelineContextValue {
  const context = useContext(TimelineContext)
  if (!context) {
    throw new Error('useTimelineContext must be used within a TimelineProvider')
  }
  return context
}

/**
 * Hook to optionally access timeline context (returns null if outside provider)
 */
export function useOptionalTimelineContext(): TimelineContextValue | null {
  return useContext(TimelineContext)
}

export { DEFAULT_ZOOM_CONSTRAINTS }
